import React, { useEffect } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  ButtonBaseProps,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { positions } from '@mui/system';
import { SignupPageStep } from '../..';
import { verifyDataFormat } from '../../../common/CommonTools';
import { useAppSelector } from '../../../store/hooks';
import { setAppPageStep } from '../../../store/reducer/appStateSlice';
import {
  selectSignupData,
  setSignupData,
} from '../../../store/reducer/signupDataSlice';
import { outsideComponentDispatch } from '../../../store/store';
import { useTncDialog } from '../../subscription/choosePaymentMethod/desktop/TncDialog';

export interface CreateParentProfileElementInterface {
  firstNameInput: JSX.Element;
  lastNameInput: JSX.Element;
  phoneNumberInput: JSX.Element;
  passwordInput: JSX.Element;
  continueButton: JSX.Element;
  backButton: JSX.Element;
  backButtonClickHandler: () => void;
  tncButtonClickHandler: () => void;
  tncDialog: JSX.Element;
  ppButtonClickHandler: () => void;
}

export const CreateParentProfileElement =
  (): CreateParentProfileElementInterface => {
    // Redux
    const signupData = useAppSelector(selectSignupData);

    // Hooks
    const [firstNameInputError, setFirstNameInputError] = React.useState(false);
    const [lastNameInputError, setLastNameInputError] = React.useState(false);
    const [phoneNumberInputError, setPhoneNumberInputError] =
      React.useState(false);
    const [passwordInputError, setPasswordInputError] =
      React.useState<string>();
    const [showPassword, setShowPassword] = React.useState(false);
    const [passwordValue, setPasswordValue] = React.useState('');
    const { openTnc, tncDialog } = useTncDialog();

    // Handlers
    const createParentProfileBackButtonClickHandler = (): void => {
      console.debug('CLICKED BACK BUTTON');
      outsideComponentDispatch(
        setAppPageStep(SignupPageStep.CREATE_PARENT_ACCOUNT),
      );
    };

    const handlePhoneNumberChange = (
      countryCode: string,
      phoneNumber: string,
    ): void => {
      const validCountryCode = verifyDataFormat(countryCode, {
        type: 'PHONENUMBERCOUNTRYCODE',
      });

      const validPhoneNumber = verifyDataFormat(countryCode + phoneNumber, {
        type: 'PHONENUMBER',
      });

      setPhoneNumberInputError(!(validCountryCode && validPhoneNumber));

      outsideComponentDispatch(
        setSignupData({
          dataKey: 'phoneNumberCountryCode',
          data: countryCode,
        }),
      );

      outsideComponentDispatch(
        setSignupData({
          dataKey: 'phoneNumberNumber',
          data: phoneNumber,
        }),
      );
    };

    const createParentProfileNextButtonHandler = (): void => {
      outsideComponentDispatch(setAppPageStep(SignupPageStep.YOUR_CHILD_AGE));
    };

    useEffect(() => {
      const keyDownHandler = (event: KeyboardEvent): void => {
        if (event.key === 'Enter') {
          event.preventDefault();
          createParentProfileNextButtonHandler();
        }
      };

      document.addEventListener('keydown', keyDownHandler);

      return (): void => {
        document.removeEventListener('keydown', keyDownHandler);
      };
    }, []);

    return {
      firstNameInput: (
        <TextField
          sx={{ width: 'inherit' }}
          fullWidth={true}
          color="primary"
          variant="outlined"
          type="text"
          label="First Name"
          size="medium"
          margin="none"
          defaultValue={signupData.firstname}
          error={firstNameInputError}
          helperText={firstNameInputError && 'Invalid first name'}
          onChange={(event) => {
            if (!verifyDataFormat(event.target.value, { type: 'NAME' })) {
              setFirstNameInputError(true);
              outsideComponentDispatch(
                setSignupData({ dataKey: 'firstname', data: '' }),
              );
              return;
            }

            setFirstNameInputError(false);
            outsideComponentDispatch(
              setSignupData({ dataKey: 'firstname', data: event.target.value }),
            );
          }}
        />
      ),
      lastNameInput: (
        <TextField
          sx={{ width: 'inherit' }}
          fullWidth={true}
          color="primary"
          variant="outlined"
          type="text"
          label="Last Name"
          size="medium"
          margin="none"
          error={lastNameInputError}
          helperText={lastNameInputError && 'Invalid last name'}
          defaultValue={signupData.lastname}
          onChange={(event) => {
            if (!verifyDataFormat(event.target.value, { type: 'NAME' })) {
              setLastNameInputError(true);
              outsideComponentDispatch(
                setSignupData({ dataKey: 'lastname', data: '' }),
              );
              return;
            }

            setLastNameInputError(false);
            outsideComponentDispatch(
              setSignupData({ dataKey: 'lastname', data: event.target.value }),
            );
          }}
        />
      ),
      phoneNumberInput: (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            position: 'relative',
          }}
        >
          <FormControl sx={{ width: 153 }} variant="outlined">
            <InputLabel color="primary">Country</InputLabel>
            <Select
              name="tel-country-code"
              color="primary"
              size="medium"
              label="Country"
              defaultValue={signupData.phoneNumberCountryCode}
              onChange={(event): void => {
                handlePhoneNumberChange(
                  event.target.value,
                  signupData.phoneNumberNumber,
                );
              }}
              renderValue={(value): string => {
                switch (value) {
                  case '+852':
                    return '🇭🇰 +852';
                  case '+886':
                    return '🇹🇼 +886';
                  case '+86':
                    return '🇨🇳 +86';
                  case '+60':
                    return '🇲🇾 +60';
                  case '+63':
                    return '🇵🇭 +63';
                  case '+65':
                    return '🇸🇬 +65';
                  case '+44':
                    return '🇬🇧 +44';
                  case '+1':
                    return '🇺🇸 +1';
                  case '+84':
                    return '🇻🇳 +84';
                  default:
                    return 'err';
                }
              }}
            >
              <MenuItem value={'+852'}>🇭🇰 Hong Kong +852</MenuItem>
              <MenuItem value={'+886'}>🇹🇼 Taiwan +886</MenuItem>
              <MenuItem value={'+86'}>🇨🇳 China +86</MenuItem>
              <MenuItem value={'+60'}>🇲🇾 Malaysia +60</MenuItem>
              <MenuItem value={'+63'}>🇵🇭 Philippines +63</MenuItem>
              <MenuItem value={'+65'}>🇸🇬 Singapore +65</MenuItem>
              <MenuItem value={'+44'}>🇬🇧 United Kingdom +44</MenuItem>
              <MenuItem value={'+1'}>🇺🇸 United States +1</MenuItem>
              <MenuItem value={'+84'}>🇻🇳 Viet Nam +84</MenuItem>
            </Select>
            <FormHelperText />
          </FormControl>
          <TextField
            id="tel-national"
            name="tel-national"
            autoComplete="tel-national"
            sx={{ width: 279, flexGrow: 1 }}
            color="primary"
            variant="outlined"
            type="tel"
            label="Phone Number"
            size="medium"
            margin="none"
            error={phoneNumberInputError}
            defaultValue={signupData.phoneNumberNumber}
            helperText={phoneNumberInputError && 'Invalid phone number'}
            onChange={(event): void => {
              handlePhoneNumberChange(
                signupData.phoneNumberCountryCode,
                event.target.value,
              );
            }}
          />
        </Box>
      ),
      passwordInput: (
        <>
          <input style={{ position: 'absolute', opacity: 0 }} tabIndex={-1} />
          <FormControl sx={{ m: 0, width: '100%' }} variant="outlined">
            <InputLabel htmlFor="outlined-adornment-password">
              Password
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              name="new-password"
              autoComplete="new-password"
              type={showPassword ? 'text' : 'password'}
              sx={{ width: 'inherit' }}
              fullWidth={true}
              error={passwordInputError !== undefined}
              defaultValue={signupData.password}
              onChange={(event): void => {
                if (
                  !verifyDataFormat(
                    event.target.value,
                    { type: 'PASSWORD' },
                    setPasswordInputError,
                  )
                ) {
                  outsideComponentDispatch(
                    setSignupData({ dataKey: 'password', data: '' }),
                  );
                  return;
                }

                setPasswordInputError(undefined);
                outsideComponentDispatch(
                  setSignupData({
                    dataKey: 'password',
                    data: event.target.value,
                  }),
                );
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
            />
            <FormHelperText
              error={passwordInputError !== undefined}
              id="accountId-error"
            >
              {passwordInputError ??
                'Password must be at least 8 characters, contain lowercase, uppercase and numbers.'}
            </FormHelperText>
          </FormControl>
        </>
      ),
      continueButton: (
        <Button
          variant="contained"
          sx={{ width: 'inherit' }}
          fullWidth={true}
          disabled={
            firstNameInputError ||
            lastNameInputError ||
            phoneNumberInputError ||
            passwordInputError !== undefined ||
            signupData.firstname === '' ||
            signupData.lastname === '' ||
            signupData.phoneNumberCountryCode === '' ||
            signupData.phoneNumberNumber === '' ||
            signupData.password === ''
          }
          onClick={createParentProfileNextButtonHandler}
        >
          Continue
        </Button>
      ),
      backButton: (
        <Button
          variant="text"
          sx={{ width: 'inherite' }}
          fullWidth={true}
          onClick={createParentProfileBackButtonClickHandler}
        >
          Back
        </Button>
      ),
      backButtonClickHandler: createParentProfileBackButtonClickHandler,
      tncButtonClickHandler: openTnc,
      tncDialog,
      ppButtonClickHandler(): void {
        console.debug('CLICKED PP PAGE');
        window.open('/PrivacyPolicy', '_blank');
      },
    };
  };
